import { IPrimaryUser } from "services/api/api.types";
import { useToast } from "components/atoms/toast/toast-context";
import {
  getJoinClassLink,
  getModalConfiguration
} from "services/api/sessions/sessionsService";
import { useEffect, useState } from "react";
import {
  ClassSession,
  ISessionModalConfigurationResponseWithAPIandState,
  SubscriptionStatus,
  SubscriptionType,
  UpcomingClassSession
} from "services/api/sessions/sessionService.types";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useLiveClasses } from "hooks/use-live-classes/use-live-classes";
import {
  getAllClasses,
  getMandatoryAndExtraClasses,
  getOngoingAndUpcomingClasses,
  getTimeStampFromDateString
} from "../../utils";
import {
  ClassSessionType,
  IFeedbackTrialClassDataObject,
  IUseUserLanding
} from "./use-user-landing.types";
import { kelasLiveEvents } from "services/mixpanel/kelas-live-events";
import trackEvent from "services/mixpanel/mixpanel";
import { useTrialActivationModal } from "components/kelas-live/trial-activation-modal/use-trial-activation";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage
} from "services/local-storage";
import { constants } from "constants/constants";
import { getPageName, isObjectEmpty } from "utils/utils";
import { TrialActivationState } from "components/kelas-live/trial-activation-modal/trial-activation-modal.types";
import { getUserFeedback } from "services/api/nps/npsService";
import { useRouter } from "next/router";
import useRecursiveTimeout from "hooks/use-recursive-timeout/use-recursive-timeout";
import { useTimeout } from "hooks/use-timeout/use-timeout";
import { useAuth } from "stores/auth/auth-context";
import * as Sentry from "@sentry/nextjs";
import { mergeToGrowthbookAttributes } from "utils/growthbook-utils";
import { trackClassmonEvent } from "services/api/classmon/classmonService";
import { useKelasLiveContext } from "stores/kelas-live/kelas-live-context";

const useUserLanding = (
  primaryUser: IPrimaryUser,
  userSubscriptionStatus: SubscriptionStatus
): IUseUserLanding => {
  const router = useRouter();
  const {
    qna: { token, setToken }
  } = useAuth();
  const userSubscriptionType = userSubscriptionStatus.subscriptionType;
  const { initializeTimeout, killTimeout } = useTimeout();
  const {
    activationState,
    setActivationState,
    showTrialActivationComponent,
    activateTrial,
    handleActivationStates,
    showTrialActivation,
    setShowTrialActivation
  } = useTrialActivationModal(primaryUser.access_token);
  const { userSubscription } = useKelasLiveContext();
  const [isNpsModalOpen, setIsNpsModalOpen] = useState(false);
  const [isPollingActive, setIsPollingActive] = useState(false);
  const [modalConfiguration, setModalConfiguration] =
    useState<ISessionModalConfigurationResponseWithAPIandState>({
      state: {
        show_thank_you_modal: false,
        show_welcome_modal: false,
        slot_opt_in_date: ""
      },
      api: {
        show_thank_you_modal: false,
        show_welcome_modal: false,
        slot_opt_in_date: ""
      }
    });
  const [feedbackTrialClassDataObject, setFeedbackTrialClassDataObject] =
    useState<IFeedbackTrialClassDataObject | null>(null);

  const getTrialClassFeedbackDetails = async () => {
    const { live_class_id } = router.query;
    if (!live_class_id) {
      try {
        const { data } = await getUserFeedback(primaryUser.access_token);
        setFeedbackTrialClassDataObject(data);
        if (data.id) {
          setIsPollingActive(false);
        }
      } catch (error) {
        setIsPollingActive(false);
        Sentry.captureException(error);
      }
    } else {
      setFeedbackTrialClassDataObject({ id: live_class_id as string });
    }
    setIsNpsModalOpen(true);
  };

  const { kill } = useRecursiveTimeout<void>(
    getTrialClassFeedbackDetails,
    constants.CHECK_NPS_POLLING_RATE,
    isPollingActive
  );

  const handleNpsModalClose = () => {
    setIsNpsModalOpen(false);
    setIsPollingActive(false);
    kill();
  };

  useEffect(() => {
    const kelasLiveTrialActivation = getLocalStorage(
      constants.KELAS_LIVE_TRIAL_ACTIVATION
    ) as {
      activate: boolean;
    };
    if (!isObjectEmpty(kelasLiveTrialActivation)) {
      switch (userSubscriptionType) {
        case SubscriptionType.FREE: {
          activateTrial();
          setActivationState(TrialActivationState.TRIAL_ACTIVATED);
          break;
        }
        case SubscriptionType.TRIAL: {
          handleActivationStates(TrialActivationState.TRIAL_ACTIVE);
          break;
        }
        case SubscriptionType.FREE_AFTER_TRIAL: {
          handleActivationStates(TrialActivationState.TRIAL_EXPIRED);
          break;
        }
        case SubscriptionType.FREE_AFTER_PAID: {
          handleActivationStates(TrialActivationState.PAID_EXPIRED);
          break;
        }
      }
      removeLocalStorage(constants.KELAS_LIVE_TRIAL_ACTIVATION);
    }
    getTrialClassFeedbackDetails();
    return () => {
      killTimeout();
    };
  }, []);

  const [onGoingClasses, setonGoingClasses] = useState<ClassSession[] | null>(
    null
  );

  const [upcomingClasses, setUpcomingClasses] =
    useState<UpcomingClassSession | null>(null);

  const [modalClassSession, setModalClassSession] =
    useState<ClassSession | null>(null);

  const [modalType, setModalType] = useState<ClassSessionType | null>(null);

  const [showJoiningClassPopUp, setShowJoiningClassPopUp] = useBoolean();

  const { addToast } = useToast();
  const { sessions, isFetchingSessions, classSchedule } =
    useLiveClasses(primaryUser);

  useEffect(() => {
    trackEvent({
      eventName: kelasLiveEvents.bimbelTabViewed,
      payload: {
        userType: userSubscriptionType,
        ongoingClassSectionPresent: "unavailable",
        liveClassFeedbackSectionExists: "unavailable",
        nextClassTimeHighlighted: true,
        liveHomePageDesignVersion:
          "Version 4 - Disabled Join CTA on Pinned Class Card"
      }
    });
  }, []);

  useEffect(() => {
    const { courseMappedSessions, gradeMappedSessions } = sessions;
    const courseClasses = courseMappedSessions
      ? getAllClasses(courseMappedSessions.courses)
      : [];
    const gradeClasses = gradeMappedSessions
      ? gradeMappedSessions.sessions
      : [];

    const [onGoingCourseClass, upComingCourseClass] =
      getOngoingAndUpcomingClasses(
        courseClasses,
        courseMappedSessions?.currentTimeStamp || ""
      );

    const [onGoingGradeClass, upComingGradeClass] =
      getOngoingAndUpcomingClasses(
        gradeClasses,
        gradeMappedSessions?.currentTimeStamp || ""
      );
    const [mandatoryClasses, extraClasses] =
      getMandatoryAndExtraClasses(upComingCourseClass);

    const courseAndGradeExtraClasses = extraClasses
      .concat(upComingGradeClass)
      .sort(
        (a, b) =>
          getTimeStampFromDateString(a.startDate) -
          getTimeStampFromDateString(b.startDate)
      );

    setonGoingClasses(
      onGoingCourseClass
        .concat(onGoingGradeClass)
        .sort(
          (a: ClassSession, b: ClassSession) =>
            Number(a.isOptional) - Number(b.isOptional)
        )
    );

    setUpcomingClasses({
      mandatoryClasses,
      extraClasses: courseAndGradeExtraClasses
    });
  }, [sessions]);

  useEffect(() => {
    const slotIds: Array<string> = [];
    onGoingClasses?.map((session) => {
      slotIds.push(session.slotId);
    });
    if (slotIds.length > 0) {
      mergeToGrowthbookAttributes({ slot_id: slotIds[0] });
    }
  }, [onGoingClasses]);

  const getJoinLinkURL = async (classID: string) => {
    trackClassmonEvent("joining", primaryUser.id, classID);

    const pageName = getPageName();

    trackEvent({
      eventName: kelasLiveEvents.liveClassJoinButtonPressed,
      payload: {
        userType: userSubscriptionType,
        incomingSource: pageName,
        liveHomePageDesignVersion: "Version 2 - Blue Bulletin Board",
        buttonStatus: "Enabled",
        source: "Pinned Class Card"
      }
    });
    setShowJoiningClassPopUp.on();
    setClassSessionModalDetails(null, null);
    try {
      const joinClassResponse = await getJoinClassLink(
        primaryUser.access_token,
        classID,
        primaryUser.id
      );
      const zoomMeetingLink = joinClassResponse.data.join_url;
      trackEvent({
        eventName: kelasLiveEvents.liveClassJoined,
        payload: {
          userType: userSubscriptionType
        }
      });
      setShowJoiningClassPopUp.off();
      setToken(joinClassResponse.data.qna_token);
      setTimeout(() => {
        window.open(zoomMeetingLink, "_blank");
      });
    } catch (err) {
      const error = err as { json?: { errors?: string[] } };
      if (
        error.json?.errors?.includes(
          "LiveClassErrors::StudentNotEligibleForClass"
        ) &&
        primaryUser?.access_token
      ) {
        userSubscription.fetchSubscriptionStatus(primaryUser?.access_token);
      } else {
        addToast({
          heading: "Something went wrong, Try again",
          variant: "error"
        });
        Sentry.captureException(err);
        trackEvent({
          eventName: kelasLiveEvents.liveClassJoinError,
          payload: {
            userType: userSubscriptionType,
            error: JSON.stringify(err)
          }
        });
      }
      setShowJoiningClassPopUp.off();
    }
  };

  const openQnALink = async (classID: string) => {
    try {
      if (!token) {
        const joinClassResponse = await getJoinClassLink(
          primaryUser.access_token,
          classID,
          primaryUser.id
        );
        const qnaToken = joinClassResponse.data.qna_token;
        setToken(qnaToken);
      }
      window.open(`/bimbel/user/qna/${classID}`, "_blank");
    } catch (err) {
      Sentry.captureException(err);
      addToast({
        heading: "Something went wrong while opening qna",
        variant: "error"
      });
    }
  };

  const routeToWeeklySchedulePage = () => {
    trackEvent({
      eventName: kelasLiveEvents.liveClassSeeAllClicked
    });
    router.push({
      pathname: "/bimbel/user/weekly_schedule",
      query: router.query
    });
  };

  const handleJoinClass = (classSession: ClassSession) => {
    if (userSubscriptionType !== SubscriptionType.FREE) {
      getJoinLinkURL(classSession.id);
      initializeTimeout(
        () => setIsPollingActive(true),
        constants.CHECK_NPS_POLLING_DELAY
      );
    } else {
      setShowTrialActivation.on();
    }
  };

  const setClassSessionModalDetails = (
    classSession: ClassSession | null,
    classType: ClassSessionType | null
  ) => {
    setModalClassSession(classSession);
    setModalType(classType);
  };

  const getPaidUserModalConfiguration = async () => {
    try {
      const res = await getModalConfiguration(primaryUser.access_token);
      setModalConfiguration({
        api: {
          show_welcome_modal: res.show_welcome_modal,
          show_thank_you_modal: res.show_thank_you_modal,
          slot_opt_in_date: res.slot_opt_in_date
        },
        state: {
          show_welcome_modal:
            (getLocalStorage(
              `${constants.BIMBEL_WELCOME_MODAL_CLICKED}_${primaryUser.id}`
            ) as boolean) === true
              ? false
              : res.show_welcome_modal,
          show_thank_you_modal:
            (getLocalStorage(
              `${constants.BIMBEL_WELCOME_MODAL_THANKYOU_CLICKED}_${primaryUser.id}`
            ) as boolean) === true
              ? false
              : res.show_thank_you_modal,
          slot_opt_in_date: res.slot_opt_in_date
        }
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    getPaidUserModalConfiguration();
  }, [primaryUser]);

  const updateModalConfigurationThankyouScreen = () => {
    setLocalStorage(
      `${constants.BIMBEL_WELCOME_MODAL_THANKYOU_CLICKED}_${primaryUser.id}`,
      true
    );

    setModalConfiguration({
      ...modalConfiguration,
      state: {
        show_thank_you_modal: false,
        show_welcome_modal: modalConfiguration.state.show_welcome_modal,
        slot_opt_in_date: modalConfiguration.state.slot_opt_in_date
      }
    });
  };

  return {
    getJoinLinkURL,
    onGoingClasses,
    upcomingClasses,
    modalClassSession,
    setClassSessionModalDetails,
    modalType,
    showJoiningClassPopUp,
    sessions,
    mediaBaseUrl: sessions.courseMappedSessions
      ? sessions.courseMappedSessions.mediaBaseUrl
      : null,
    currentTimeStamp: sessions.courseMappedSessions
      ? sessions.courseMappedSessions.currentTimeStamp
      : null,
    activationState,
    setActivationState,
    showTrialActivationComponent,
    activateTrial,
    handleActivationStates,
    showTrialActivation,
    setShowTrialActivation,
    handleJoinClass,
    isNpsModalOpen,
    feedbackTrialClassDataObject,
    handleNpsModalClose,
    routeToWeeklySchedulePage,
    isFetchingSessions,
    openQnALink,
    modalConfiguration,
    updateModalConfigurationThankyouScreen,
    classSchedule
  };
};

export { useUserLanding };
